// The decisions of the chunked video upload, with no browser in them: how big
// the next chunk is, when and how long to wait before sending one again, how
// far along the upload is, what may be picked, and what state an upload is in
// and what that state says. videoUploader.js runs the requests and the timers
// and asks this file what to do next; nothing here reads a clock, a random
// number or the network, so every rule can be tested with literals.
//
// The numbers the server owns (the size limit, the accepted types, how many
// videos a message may carry, the chunk range it will take) come from
// GET video_upload.php?action=config and are passed in as `policy`; this file
// mirrors none of them except the browser's own comfort range for a chunk.
import { formatAttachmentSize, isVideoLikeName } from '@/utils/messageAttachments';

const KiB = 1024;
const MiB = 1024 * KiB;

// A chunk is 1 MiB to begin with and then follows the connection: about eight
// seconds of what the last one achieved, between 256 KiB and 8 MiB, in steps of
// 64 KiB. keyon takes 128 KiB to 8 MiB (the last chunk may be smaller); the
// 256 KiB floor is ours, so a slow link does not turn into thousands of
// requests against the endpoint's own rate counter.
export const CHUNK_INITIAL = 1 * MiB;
export const CHUNK_MIN = 256 * KiB;
export const CHUNK_MAX = 8 * MiB;
const CHUNK_STEP = 64 * KiB;
const CHUNK_TARGET_MS = 8000;

// A chunk that makes no progress for this long is abandoned and sent again.
export const NO_PROGRESS_MS = 20000;

// Six waits before a chunk is given up on and the person is asked.
const BACKOFF_MS = [1000, 2000, 4000, 8000, 15000, 30000];
export const RETRY_LIMIT = BACKOFF_MS.length;

const THROUGHPUT_ALPHA = 0.3;

function positive(value) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

/**
 * The chunk range for one upload: ours, narrowed by what the server's config
 * says it takes. A value that is missing or not a number is ours.
 * @returns {{min:number, initial:number, max:number}}
 */
export function chunkLimits(policy) {
  const p = policy && typeof policy === 'object' ? policy : {};
  const min = Math.max(CHUNK_MIN, positive(p.chunk_min) || 0);
  const max = Math.max(min, Math.min(CHUNK_MAX, positive(p.chunk_max) || CHUNK_MAX));
  return { min, initial: clamp(positive(p.chunk_initial) || CHUNK_INITIAL, min, max), max };
}

const DEFAULT_LIMITS = Object.freeze({ min: CHUNK_MIN, initial: CHUNK_INITIAL, max: CHUNK_MAX });

/**
 * The size of the next chunk.
 * @param {null|{size:number, failed:true}|{size?:number, bytes:number, ms:number}} previous
 *   null for the first chunk; a failure of a chunk of `size` bytes halves it; a
 *   success says how many bytes the last chunk carried and how long it took
 * @param {{min:number, initial:number, max:number}} [limits]
 */
export function nextChunkSize(previous, limits = DEFAULT_LIMITS) {
  if (!previous) return clamp(limits.initial, limits.min, limits.max);
  const stepped = (n) => Math.floor(n / CHUNK_STEP) * CHUNK_STEP;
  if (previous.failed) return clamp(stepped(Math.floor(previous.size / 2)), limits.min, limits.max);
  // A chunk that took no time at all is not a division by zero: it is as fast as the cap allows.
  const target = (previous.bytes * CHUNK_TARGET_MS) / Math.max(previous.ms, 1);
  return clamp(stepped(target), limits.min, limits.max);
}

/**
 * How long to wait before the next attempt at a chunk: the nth step (1, 2, 4, 8,
 * 15, 30 seconds) with half of it to all of it, so a hundred phones that lost
 * the network together do not come back together. After the sixth there is no
 * seventh: null, and the upload stops and offers a retry.
 * @param {number} attempt 0 for the wait after the first failure
 * @param {() => number} random a number in [0, 1), injected
 */
export function retryDelayMs(attempt, random = Math.random) {
  if (!Number.isInteger(attempt) || attempt < 0 || attempt >= BACKOFF_MS.length) return null;
  return Math.round(BACKOFF_MS[attempt] * (0.5 + 0.5 * random()));
}

/**
 * The exponential moving average of the throughput in bytes a second.
 * @param {number|null} previous the average so far, null before the first chunk
 */
export function updateThroughput(previous, bytes, ms) {
  const sample = (bytes * 1000) / Math.max(ms, 1);
  return previous === null || previous === undefined ? sample : THROUGHPUT_ALPHA * sample + (1 - THROUGHPUT_ALPHA) * previous;
}

/** Seconds left at the average speed, or null while there is no speed to go on. */
export function etaSeconds(remainingBytes, bytesPerSecond) {
  const rate = positive(bytesPerSecond);
  if (rate === null) return null;
  return Math.ceil(Math.max(0, remainingBytes) / rate);
}

/** A whole percent from 0 to 100. */
export function progressPercent(sent, total) {
  if (!(total > 0)) return 0;
  return clamp(Math.floor((sent / total) * 100), 0, 100);
}

/** The time left in a few words, in steps a person does not watch tick. */
export function formatEta(seconds) {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) return '';
  if (seconds < 10) return 'a few seconds';
  if (seconds < 60) {
    const rounded = Math.round(seconds / 5) * 5;
    return rounded < 60 ? `about ${rounded} s` : 'about 1 min';
  }
  if (seconds < 3600) return `about ${Math.ceil(seconds / 60)} min`;
  return 'over an hour';
}

/** "Uploading 42% - about 40 s left", with the second half once there is a speed to base it on. */
export function uploadProgressText({ percent, etaSeconds: eta } = {}) {
  const head = `Uploading ${Number.isFinite(percent) ? percent : 0}%`;
  const left = formatEta(eta);
  return left ? `${head} - ${left} left` : head;
}

/**
 * What lets a later pick of the same file carry on where this one stopped: the
 * sha256 of its name, size, last-modified time and the person. keyon looks a
 * session up by it, inside the account and the user, and continues an open one
 * that is the same upload. The type is left out on purpose (a browser may guess
 * it differently next time), and a browser that gives no time counts as zero.
 * @param {(text: string) => Promise<string>} sha256Hex lowercase hex of the text's utf-8 bytes
 */
export async function resumeKey(file, uid, sha256Hex) {
  const lastModified = Number.isFinite(file.lastModified) ? file.lastModified : 0;
  return sha256Hex(`${file.name}|${file.size}|${lastModified}|${uid}`);
}

// What a picked file may be. The accepted types and the numbers come from the
// server's config; these defaults only keep a config that lacks them from
// turning the check into "accept anything".
const DEFAULT_VIDEO_EXTENSIONS = ['mp4', 'm4v', 'mov'];
const DEFAULT_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/x-m4v'];

function listOf(value, fallback) {
  return Array.isArray(value) && value.length ? value.map((v) => String(v).toLowerCase().replace(/^\./, '')) : fallback;
}

/**
 * Whether a picked file may be uploaded as a video, in words a person can act
 * on. A type or an extension that contradicts the accepted ones (a .mkv that a
 * browser calls video/mp4) is refused: keyon judges the bytes, but this saves a
 * hundred megabytes on a file that will not be taken.
 * @param {{name:string, type?:string, size:number}} file
 * @param {object} policy GET video_upload.php?action=config's answer
 * @param {{videosInMessage?:number}} [context] how many videos the message already carries
 * @returns {{ok:true}|{ok:false, code:'unsupported_video'|'too_many_videos'|'video_too_large'|'empty_file', message:string}}
 */
export function validateVideoFile(file, policy, { videosInMessage = 0 } = {}) {
  const p = policy && typeof policy === 'object' ? policy : {};
  const name = String((file && file.name) || 'video');
  const type = String((file && file.type) || '').toLowerCase();
  const extensions = listOf(p.extensions, DEFAULT_VIDEO_EXTENSIONS);
  const types = listOf(p.mime_types, DEFAULT_VIDEO_TYPES).map((t) => t.replace(/^\./, ''));
  const dot = name.lastIndexOf('.');
  const ext = dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
  const extOk = ext !== '' && extensions.includes(ext);
  const typeOk = type !== '' && types.includes(type);
  const extContradicts = ext !== '' && !extOk && isVideoLikeName(name);
  const typeContradicts = type.startsWith('video/') && !typeOk;
  if (!(extOk || typeOk) || extContradicts || typeContradicts) {
    return {
      ok: false,
      code: 'unsupported_video',
      message: `"${name}" is not a video that can be sent here. Choose an MP4 or MOV video.`,
    };
  }
  const perMessage = positive(p.videos_per_message) || 1;
  if (videosInMessage >= perMessage) {
    return {
      ok: false,
      code: 'too_many_videos',
      message: perMessage === 1 ? 'Only one video can be attached to a message.' : `Up to ${perMessage} videos can be attached to a message.`,
    };
  }
  const size = Number(file.size);
  if (!(size > 0)) {
    return { ok: false, code: 'empty_file', message: `"${name}" is empty. Choose a different video.` };
  }
  const max = positive(p.max_bytes);
  if (max !== null && size > max) {
    return {
      ok: false,
      code: 'video_too_large',
      message: `"${name}" is ${formatAttachmentSize(size)}, and a video can be at most ${formatAttachmentSize(max)}. Choose a shorter video, or export it at a lower quality.`,
    };
  }
  return { ok: true };
}

// ---------------------------------------------------------------------------
// The life of one upload
// ---------------------------------------------------------------------------

export const UPLOAD_STATUSES = [
  'checking', 'starting', 'uploading', 'waiting_network', 'paused_mfa', 'finalizing', 'ready', 'failed', 'cancelled',
];
const IN_FLIGHT = ['checking', 'starting', 'uploading', 'waiting_network', 'paused_mfa', 'finalizing'];

/** Running, or parked and waiting for the network or the second factor. */
export function isUploadInFlight(status) {
  return IN_FLIGHT.includes(status);
}

/**
 * Whether a send must wait for this upload: anything that is not ready,
 * a failed one included. Sending while a failed upload sits in the composer
 * would send the message without the video the person attached.
 */
export function isUploadBusy(status) {
  return status !== 'ready';
}

export function initialUploadState() {
  return { status: 'checking', code: null, message: null, fileUid: null, resumeTo: null };
}

/**
 * The next state of an upload for an event, or the same object when the event
 * means nothing where the upload is. failed and cancelled are left only by an
 * explicit retry: nothing else the uploader's late callbacks say can revive them.
 *
 * events: validated, started, all_sent, resend, offline, online, mfa_lapsed,
 * mfa_resumed, completed{fileUid}, fail{code,message}, cancel, retry
 */
export function reduceUploadState(state, event) {
  const s = state || initialUploadState();
  const base = { code: null, message: null, fileUid: null, resumeTo: null };
  const to = (status, extra = {}) => ({ ...base, status, ...extra });
  const type = event && event.type;
  const { status } = s;
  const running = ['starting', 'uploading', 'finalizing'].includes(status);

  switch (type) {
    case 'validated':
      return status === 'checking' ? to('starting') : s;
    case 'started':
      return status === 'starting' ? to('uploading') : s;
    case 'all_sent':
      return status === 'uploading' ? to('finalizing') : s;
    case 'resend':
      // The server says it is short of the end after all (a complete refused as
      // incomplete, or a session begun again): back to sending.
      return status === 'finalizing' ? to('uploading') : s;
    case 'offline':
      return running ? to('waiting_network', { resumeTo: status }) : s;
    case 'online':
      return status === 'waiting_network' ? to(s.resumeTo || 'uploading') : s;
    case 'mfa_lapsed':
      if (running) return to('paused_mfa', { resumeTo: status });
      return status === 'waiting_network' ? to('paused_mfa', { resumeTo: s.resumeTo || 'uploading' }) : s;
    case 'mfa_resumed':
      return status === 'paused_mfa' ? to(s.resumeTo || 'uploading') : s;
    case 'completed':
      return status === 'finalizing' ? to('ready', { fileUid: event.fileUid || null }) : s;
    case 'fail':
      return isUploadInFlight(status) ? to('failed', { code: event.code || 'unknown', message: event.message || null }) : s;
    case 'cancel':
      return isUploadInFlight(status) || status === 'ready' ? to('cancelled') : s;
    case 'retry':
      return status === 'failed' || status === 'cancelled' ? to('starting') : s;
    default:
      return s;
  }
}

// ---------------------------------------------------------------------------
// What each state and each refusal says
// ---------------------------------------------------------------------------

// The sentences of keyon's own upload refusals are copied from
// keyon/lib/UploadRefusal.php, which also sends them as `message`; the page
// shows the server's sentence when it has one and these when it has not (a
// refusal with no body, a code the answer left out, a failure of the browser's).
const CODE_SENTENCES = {
  unsupported_video: 'This video is in a format that cannot be uploaded here. Use an MP4 or MOV file from your phone or camera.',
  video_incomplete: 'This video file looks cut off or damaged. Record or export it again and upload it again.',
  video_too_complex: 'This video is too complex to accept. Export it again as a plain MP4 and upload it again.',
  video_too_large: 'This video is too large to upload. Choose a shorter video, or export it at a lower quality.',
  video_busy: 'You already have several videos uploading. Let one finish, or cancel it, and try again.',
  video_quota: 'You have reached the limit for video uploads for now. Try again later.',
  upload_session_expired: 'This upload took too long and has expired. Start the upload again.',
  incomplete_upload: 'The video has not finished uploading yet. Wait for the upload to finish, then try again.',
  chunk_corrupt: 'Part of the video arrived damaged and will be sent again.',
  upload_busy: 'This upload is busy with another request. Wait a moment and try again.',
  upload_state_lost: 'This upload cannot be continued. Start the upload again from the beginning.',
  out_of_order: 'Part of the video arrived out of order. The upload will carry on from where it left off.',
  uploads_unavailable: 'Uploads are unavailable right now. Try again shortly.',
  unavailable: 'Uploads are unavailable right now. Try again shortly.',
  upload_error: 'The file did not arrive completely. Try uploading it again.',
  unsupported_type: 'This type of file cannot be uploaded here.',
  rate_limited: 'Too many attempts. Wait a minute and try again.',
  payload_too_large: 'A part of the video was too large for the connection. Try uploading it again.',
  not_found: 'This upload could not be found. Start the upload again.',
  unauthenticated: 'Your session has ended. Sign in again, then upload the video again.',
  forbidden: 'You are not allowed to upload this video.',
  csrf: 'This page is out of date. Reload it and try again.',
  mfa_required: 'Confirm your sign-in code to continue.',
  no_crypto: 'This browser cannot prepare a video for upload here. Open the site over a secure (https) connection in an up-to-date browser.',
  file_unreadable: 'This video could not be read. If it was moved or deleted, choose it again.',
  network: 'The upload could not reach the server after several tries. Check the connection and try again.',
  unknown: 'The video could not be uploaded. Try again.',
  // A note video's start answered 404 not_found: keyon could not resolve the
  // patient inside the account (NoteWriteGate). Not the lost session not_found
  // means everywhere else, and not cured by starting again.
  patient_not_found: 'This patient could not be found for a video, so the video cannot be attached to this note.',
  // A status call found the session failed (failedSessionCode).
  video_invalid: 'This video was not accepted: it is damaged, too complex or in a format that cannot be uploaded here. Export it again as a plain MP4 and upload it again.',
  upload_failed: 'This upload could not be finished. Retry to upload the video again from the beginning.',
};

export const UPLOAD_FAILURE_CODES = Object.keys(CODE_SENTENCES);

export function uploadCodeSentence(code) {
  return Object.prototype.hasOwnProperty.call(CODE_SENTENCES, code) ? CODE_SENTENCES[code] : CODE_SENTENCES.unknown;
}

/**
 * The failure a status answer of `failed` stands for, from the session's
 * status_reason. keyon fails a session for good (a verdict on the committed
 * bytes, a person who may no longer write the note, a row or blob that does not
 * match), so it is final: beginning a new session would send the whole video
 * again. A reason this page does not know is upload_failed, which the person may
 * retry by hand.
 */
export function failedSessionCode(statusReason) {
  if (statusReason === 'video_invalid') return 'video_invalid';
  if (statusReason === 'note_access') return 'forbidden';
  return 'upload_failed';
}

// Verdicts on the file itself, or on this person's right or quota: the same
// bytes from the same person get the same answer, so a retry (a new session and
// the whole file again) cannot help, and each verdict at complete is a strike
// toward video_quota (VideoQuota, three failed completes a day). Anything else
// (the network, uploads_unavailable, upload_state_lost, a 5xx, a code nobody
// has written yet) may go another way and keeps its Retry.
const FINAL_FAILURE_CODES = [
  'unsupported_video', 'video_incomplete', 'video_too_complex', 'video_too_large', 'unsupported_type',
  'forbidden', 'video_quota', 'video_busy', 'patient_not_found', 'video_invalid',
];

/** Whether a failure with this code is final: no Retry, only the composer's Remove. */
export function isFinalUploadFailure(code) {
  return FINAL_FAILURE_CODES.includes(code);
}

/**
 * Codes that say this session is finished and a new one has to begin (a closed,
 * expired or lost session). The uploader begins one itself, once; after that
 * the person is asked.
 */
export function isRestartCode(code) {
  return code === 'upload_state_lost' || code === 'upload_session_expired';
}

const STATE_TEXT = {
  checking: 'Checking the video...',
  starting: 'Starting the upload...',
  waiting_network: 'Waiting for connection...',
  paused_mfa: 'Verify again to continue',
  finalizing: 'Finishing the upload...',
  ready: 'Ready to send',
  cancelled: 'Upload cancelled',
};

/**
 * What an upload in this state says on its chip.
 * @param {{status:string, code?:string|null, message?:string|null}} state
 * @param {{percent?:number, etaSeconds?:number|null}} [progress]
 */
export function uploadStateText(state, progress = {}) {
  const { status } = state || {};
  if (status === 'uploading') return uploadProgressText(progress);
  if (status === 'failed') {
    const sentence = typeof state.message === 'string' ? state.message.trim() : '';
    return sentence || uploadCodeSentence(state.code);
  }
  return STATE_TEXT[status] || '';
}

/**
 * The buttons a chip offers in this state: cancel, verify (sign-in code again)
 * and retry. A failure that is final offers none: every composer puts its own
 * Remove next to the chip, and that is the way out.
 */
export function uploadChipActions(status, code = null) {
  if (status === 'paused_mfa') return ['verify', 'cancel'];
  if (status === 'failed') return isFinalUploadFailure(code) ? [] : ['retry'];
  return isUploadInFlight(status) ? ['cancel'] : [];
}
